import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AIArticleGenerator from '../Resources/AIArticleGenerator';
import { fetchProviderStatus, setProviderKey, clearProviderKey } from '../../services/aiGenerationService';
import {
  loadAiSettingsLocal,
  saveAiSettings,
  fetchSettings,
  AI_SETTINGS_DEFAULTS,
} from '../../services/globalSettingsService';

const {
  FiBookOpen, FiCpu, FiKey, FiEye, FiEyeOff, FiSave, FiTrash2,
  FiCheck, FiAlertTriangle, FiInfo, FiCode, FiLink, FiShield,
  FiZap, FiRefreshCw, FiSettings, FiServer,
  FiChevronDown, FiChevronUp, FiWifi, FiWifiOff,
  FiExternalLink, FiCloud, FiCloudOff,
} = FiIcons;

// ── Provider definitions ──────────────────────────────────────────────────────
const PROVIDERS = [
  {
    id: 'anthropic', label: 'Claude (Anthropic)',
    envKey: 'ANTHROPIC_API_KEY', keyPrefix: 'sk-ant-',
    placeholder: 'sk-ant-api03-...',
    gradient: 'from-orange-500 to-amber-600',
    badge: 'Recommended · MCP Support',
    badgeColor: 'bg-orange-100 text-orange-700',
    docsUrl: 'https://console.anthropic.com/settings/keys',
    models: ['claude-opus-4-5', 'claude-sonnet-4-5', 'claude-3-5-sonnet-20241022', 'claude-3-haiku-20240307'],
    mcpSupported: true,
  },
  {
    id: 'openai', label: 'GPT (OpenAI)',
    envKey: 'OPENAI_API_KEY', keyPrefix: 'sk-',
    placeholder: 'sk-proj-...',
    gradient: 'from-emerald-500 to-teal-600',
    badge: 'Popular',
    badgeColor: 'bg-emerald-100 text-emerald-700',
    docsUrl: 'https://platform.openai.com/api-keys',
    models: ['gpt-4o', 'gpt-4-turbo', 'gpt-4', 'gpt-3.5-turbo'],
    mcpSupported: false,
  },
  {
    id: 'deepseek', label: 'DeepSeek',
    envKey: 'DEEPSEEK_API_KEY', keyPrefix: 'sk-',
    placeholder: 'sk-...',
    gradient: 'from-blue-500 to-indigo-600',
    badge: 'Cost Efficient',
    badgeColor: 'bg-blue-100 text-blue-700',
    docsUrl: 'https://platform.deepseek.com/api_keys',
    models: ['deepseek-chat', 'deepseek-reasoner'],
    mcpSupported: false,
  },
];

const MCP_FIELDS = [
  { key: 'server_url',    label: 'MCP Server URL',         placeholder: 'https://mcp.your-server.com', icon: FiLink },
  { key: 'tool_endpoint', label: 'Tool Endpoint Path',     placeholder: '/tools/content-lookup',       icon: FiCode },
  { key: 'context_tool',  label: 'Context Injection Tool', placeholder: '/tools/legacy-context',       icon: FiCode },
];

// ── Shared UI primitives ──────────────────────────────────────────────────────
const Toggle = ({ active, onToggle, label }) => (
  <button type="button" onClick={onToggle} className="flex items-center gap-3 group">
    <div className={`w-11 h-6 rounded-full relative flex-shrink-0 transition-colors duration-300 ${
      active ? 'bg-accent-gold' : 'bg-brand-dark'
    }`}>
      <motion.div animate={{ x: active ? 22 : 2 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm" />
    </div>
    {label && <span className="text-xs font-bold text-accent-navy group-hover:text-accent-gold transition-colors">{label}</span>}
  </button>
);

const StatusPill = ({ configured, source }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
    configured ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
  }`}>
    <SafeIcon icon={configured ? FiWifi : FiWifiOff} className="w-3 h-3" />
    {configured ? `Key: ${source}` : 'No Key'}
  </span>
);

// ── Save Toast ────────────────────────────────────────────────────────────────
const SaveToast = ({ show, message, isWarning = false }) => (
  <AnimatePresence>
    {show && (
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.9 }}
        className={`fixed bottom-8 right-8 z-[400] text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-3 ${
          isWarning ? 'bg-amber-500' : 'bg-green-500'
        }`}
      >
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={isWarning ? FiCloudOff : FiCheck} className="w-4 h-4" />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-widest">{isWarning ? 'Saved Locally' : 'Saved'}</p>
          <p className="text-[10px] opacity-80 max-w-[260px]">{message}</p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ── Provider Key Card ─────────────────────────────────────────────────────────
const ProviderKeyCard = ({ provider, status, adminToken, onRefresh }) => {
  const [expanded,  setExpanded]  = useState(false);
  const [apiKey,    setApiKey]    = useState('');
  const [showKey,   setShowKey]   = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [clearing,  setClearing]  = useState(false);
  const [saved,     setSaved]     = useState(false);
  const [error,     setError]     = useState('');

  const handleSave = async () => {
    if (!apiKey.trim()) return;
    setSaving(true); setError('');
    const result = await setProviderKey(provider.id, apiKey.trim(), adminToken);
    if (result.success) {
      setApiKey(''); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      onRefresh();
    } else {
      setError(result.error || 'Failed to save key.');
    }
    setSaving(false);
  };

  const handleClear = async () => {
    if (!window.confirm(`Remove the runtime key override for ${provider.label}?`)) return;
    setClearing(true);
    await clearProviderKey(provider.id, adminToken);
    onRefresh();
    setClearing(false);
  };

  const isConfigured = status?.configured;
  const isEnvSource  = status?.source === 'env';

  return (
    <motion.div layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-[24px] border-2 overflow-hidden transition-all ${
        isConfigured ? 'border-green-200 shadow-sm' : 'border-brand-dark'
      }`}>
      <div className={`bg-gradient-to-r ${provider.gradient} p-5 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiCpu} className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-serif text-white text-base">{provider.label}</h4>
              <span className={`text-[8px] px-2 py-0.5 rounded-full font-black uppercase ${provider.badgeColor}`}>{provider.badge}</span>
              {provider.mcpSupported && (
                <span className="text-[8px] px-2 py-0.5 rounded-full font-black bg-purple-100 text-purple-700">MCP</span>
              )}
            </div>
            <p className="text-white/60 text-[10px] font-mono mt-0.5">{provider.envKey}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill configured={isConfigured} source={status?.source} />
          <button onClick={() => setExpanded(v => !v)}
            className="p-2 bg-white/20 hover:bg-white/30 rounded-xl text-white transition-all">
            <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
            <div className="p-5 space-y-4">
              {isEnvSource && (
                <div className="flex items-start gap-3 p-3.5 bg-green-50 border border-green-100 rounded-2xl">
                  <SafeIcon icon={FiShield} className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-black text-green-800 uppercase tracking-widest">Key loaded from .env</p>
                    <p className="text-[10px] text-green-600 font-light mt-0.5">Set via environment variable. Use the form below to set a temporary runtime override.</p>
                  </div>
                </div>
              )}
              {status?.source === 'runtime' && (
                <div className="flex items-center justify-between p-3.5 bg-amber-50 border border-amber-100 rounded-2xl">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiZap} className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] font-black text-amber-800 uppercase tracking-widest">Runtime Override Active</p>
                      <p className="text-[10px] text-amber-600 font-light">Cleared on server restart.</p>
                    </div>
                  </div>
                  <button onClick={handleClear} disabled={clearing}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 rounded-xl text-[9px] font-black uppercase hover:bg-red-100 transition-all disabled:opacity-50">
                    {clearing ? <div className="w-3 h-3 border border-red-400 border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiTrash2} className="w-3 h-3" />}
                    Clear
                  </button>
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
                    {isConfigured ? 'Set Runtime Override' : 'Enter API Key'}
                  </label>
                  <a href={provider.docsUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[9px] text-accent-gold hover:underline font-black uppercase tracking-widest">
                    <SafeIcon icon={FiExternalLink} className="w-3 h-3" /> Get Key
                  </a>
                </div>
                <div className="relative">
                  <SafeIcon icon={FiKey} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input type={showKey ? 'text' : 'password'} value={apiKey}
                    onChange={e => setApiKey(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSave()}
                    placeholder={provider.placeholder}
                    className="w-full pl-11 pr-12 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono transition-colors" />
                  <button onClick={() => setShowKey(v => !v)} type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent-navy transition-colors">
                    <SafeIcon icon={showKey ? FiEyeOff : FiEye} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                  <SafeIcon icon={FiAlertTriangle} className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}
              <button onClick={handleSave} disabled={saving || !apiKey.trim()}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50 ${
                  saved ? 'bg-green-500 text-white' : 'bg-accent-navy text-white hover:bg-accent-navy/90'
                }`}>
                {saved ? <><SafeIcon icon={FiCheck} className="w-4 h-4" /> Key Set!</>
                  : saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  : <><SafeIcon icon={FiSave} className="w-4 h-4" /> Set Runtime Key</>}
              </button>
              <div className="pt-1">
                <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-2">Available Models</p>
                <div className="flex flex-wrap gap-1.5">
                  {provider.models.map(m => (
                    <span key={m} className="text-[9px] px-2.5 py-1 bg-brand-light rounded-lg font-mono text-accent-navy border border-brand-dark">{m}</span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── MCP Configuration Panel ───────────────────────────────────────────────────
const MCPConfigurationPanel = ({ config, onChange, onSave, saving, saved }) => (
  <div className="bg-white rounded-[24px] border border-brand-dark p-6 space-y-5">
    <div className="flex items-center gap-3 border-b border-brand-dark pb-4">
      <div className="w-10 h-10 bg-accent-navy/5 text-accent-navy rounded-2xl flex items-center justify-center flex-shrink-0">
        <SafeIcon icon={FiCode} className="w-5 h-5" />
      </div>
      <div>
        <h4 className="font-serif text-accent-navy">MCP Server Configuration</h4>
        <p className="text-[10px] text-text-muted font-light">Model Context Protocol — Claude tool-use integration</p>
      </div>
    </div>
    <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl flex items-start gap-3">
      <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-blue-700 font-light leading-relaxed">
        MCP enables Claude to call external tools during article generation. Tool execution is handled server-side via <code className="bg-blue-100 px-1 rounded font-mono">server/routes/ai.js</code>.
      </p>
    </div>
    {MCP_FIELDS.map(f => (
      <div key={f.key}>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">{f.label}</label>
        <div className="relative">
          <SafeIcon icon={f.icon} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
          <input type="text" value={config[f.key] || ''} placeholder={f.placeholder}
            onChange={e => onChange({ ...config, [f.key]: e.target.value })}
            className="w-full pl-11 pr-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono transition-colors" />
        </div>
      </div>
    ))}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {[
        { key: 'enable_tools',     label: 'Enable Tool Use'       },
        { key: 'inject_context',   label: 'Inject Legacy Context' },
        { key: 'enable_streaming', label: 'Enable Streaming'      },
        { key: 'debug_mode',       label: 'Debug Mode (Verbose)'  },
      ].map(t => (
        <div key={t.key} className="flex items-center justify-between p-3.5 bg-brand-light rounded-2xl border border-brand-dark">
          <span className="text-xs font-bold text-accent-navy">{t.label}</span>
          <Toggle active={!!config[t.key]} onToggle={() => onChange({ ...config, [t.key]: !config[t.key] })} />
        </div>
      ))}
    </div>
    <button onClick={onSave} disabled={saving}
      className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-60 ${
        saved ? 'bg-green-500 text-white' : 'bg-accent-navy text-white hover:bg-accent-navy/90'
      }`}>
      {saved ? <><SafeIcon icon={FiCheck} className="w-4 h-4" /> MCP Config Saved!</>
        : saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        : <><SafeIcon icon={FiSave} className="w-4 h-4" /> Save MCP Configuration</>}
    </button>
  </div>
);

// ── Global AI Settings Panel ──────────────────────────────────────────────────
const GlobalSettingsPanel = ({ settings, onChange, onSave, saving, saved, backendSynced }) => (
  <div className="bg-white rounded-[24px] border border-brand-dark p-6 space-y-5">
    {/* Header */}
    <div className="flex items-center justify-between gap-3 border-b border-brand-dark pb-4 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-accent-gold/10 text-accent-gold rounded-2xl flex items-center justify-center flex-shrink-0">
          <SafeIcon icon={FiSettings} className="w-5 h-5" />
        </div>
        <div>
          <h4 className="font-serif text-accent-navy">Global AI Settings</h4>
          <p className="text-[10px] text-text-muted font-light">AI generation parameters &amp; safety controls</p>
        </div>
      </div>
      <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest ${
        backendSynced
          ? 'bg-green-50 text-green-700 border border-green-200'
          : 'bg-amber-50 text-amber-700 border border-amber-200'
      }`}>
        <SafeIcon icon={backendSynced ? FiCloud : FiCloudOff} className="w-3 h-3" />
        {backendSynced ? 'Backend Synced' : 'Local Only'}
      </div>
    </div>

    {/* ── Numeric AI parameters ── */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {[
        { key: 'max_tokens',  label: 'Max Tokens Cap',       type: 'number', min: 512,  max: 128000, step: 1   },
        { key: 'temperature', label: 'Temperature',           type: 'number', min: 0,    max: 2,      step: 0.1 },
        { key: 'timeout_sec', label: 'Request Timeout (sec)', type: 'number', min: 10,   max: 300,    step: 5   },
        { key: 'retry_count', label: 'Retry Attempts',        type: 'number', min: 0,    max: 10,     step: 1   },
      ].map(f => (
        <div key={f.key}>
          <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">{f.label}</label>
          <input
            type={f.type}
            value={settings[f.key] ?? ''}
            min={f.min} max={f.max} step={f.step}
            onChange={e => onChange({
              ...settings,
              [f.key]: f.step === 1 ? parseInt(e.target.value, 10) : parseFloat(e.target.value),
            })}
            className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm transition-colors"
          />
        </div>
      ))}
    </div>

    {/* ── Boolean AI toggles ── */}
    <div className="space-y-3">
      {[
        { key: 'content_filter',    label: 'Content Filtering',    desc: 'Block inappropriate or harmful content' },
        { key: 'audit_logging',     label: 'Audit Logging',        desc: 'Log all AI requests for compliance' },
        { key: 'fallback_enabled',  label: 'Provider Fallback',    desc: 'Auto-switch to backup on failure' },
        { key: 'pii_masking',       label: 'PII Masking in Logs',  desc: 'Mask personal identifiers in logs' },
        { key: 'rate_limit_strict', label: 'Strict Rate Limiting', desc: 'Enforce tighter per-IP rate limits' },
      ].map(s => (
        <div key={s.key} className="flex items-center justify-between p-4 bg-brand-light rounded-2xl border border-brand-dark hover:border-accent-gold/30 transition-colors">
          <div>
            <p className="text-xs font-bold text-accent-navy">{s.label}</p>
            <p className="text-[10px] text-text-muted font-light">{s.desc}</p>
          </div>
          <Toggle active={!!settings[s.key]} onToggle={() => onChange({ ...settings, [s.key]: !settings[s.key] })} />
        </div>
      ))}
    </div>

    {/* ── Redirect notice for banner settings ── */}
    <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-2xl">
      <SafeIcon icon={FiInfo} className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
      <p className="text-[10px] text-blue-700 font-light leading-relaxed">
        <strong className="font-bold">Banner settings</strong> (text, theme, color, schedule, and the master on/off toggle)
        are managed exclusively in the <strong className="font-bold">Platform Maintenance Banner</strong> tab.
      </p>
    </div>

    {/* ── Save button ── */}
    <button
      onClick={onSave}
      disabled={saving}
      className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg disabled:opacity-60 ${
        saved
          ? 'bg-green-500 text-white'
          : 'bg-accent-gold text-white hover:bg-accent-gold/90 shadow-accent-gold/20'
      }`}
    >
      {saved
        ? <><SafeIcon icon={FiCheck} className="w-4 h-4" /> AI Settings Saved!</>
        : saving
          ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          : <><SafeIcon icon={FiSave} className="w-4 h-4" /> Save AI Settings</>
      }
    </button>
  </div>
);

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
const AIContentGeneration = () => {
  const [activeTab,      setActiveTab]      = useState('generate');
  const [providerStatus, setProviderStatus] = useState({});
  const [statusLoading,  setStatusLoading]  = useState(true);
  const [adminToken,     setAdminToken]     = useState(() => localStorage.getItem('ofl_admin_token') || '');
  const [tokenSaved,     setTokenSaved]     = useState(false);
  const [backendSynced,  setBackendSynced]  = useState(false);

  // ── MCP config ────────────────────────────────────────────────────────────
  const [mcpConfig, setMcpConfig] = useState(() => {
    try {
      return {
        enable_tools: false, inject_context: false,
        enable_streaming: true, debug_mode: false,
        ...JSON.parse(localStorage.getItem('ofl_mcp_config') || '{}'),
      };
    } catch {
      return { enable_tools: false, inject_context: false, enable_streaming: true, debug_mode: false };
    }
  });
  const [mcpSaving, setMcpSaving] = useState(false);
  const [mcpSaved,  setMcpSaved]  = useState(false);

  // ── AI-only global settings ───────────────────────────────────────────────
  const [globalSettings, setGlobalSettings] = useState(() => {
    // Strip any banner fields that may have been persisted previously
    const { bannerText, bannerColor, bannerTheme, ...aiOnly } = loadAiSettingsLocal();
    return { ...AI_SETTINGS_DEFAULTS, ...aiOnly };
  });
  const [globalSaving, setGlobalSaving] = useState(false);
  const [globalSaved,  setGlobalSaved]  = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState({ show: false, message: '', isWarning: false });
  const showToast = (message, isWarning = false) => {
    setToast({ show: true, message, isWarning });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 3500);
  };

  // ── On mount: hydrate AI settings from backend ────────────────────────────
  useEffect(() => {
    fetchSettings().then(data => {
      if (!data) return;
      if (data.ai) {
        const { bannerText, bannerColor, bannerTheme, ...aiOnly } = { ...AI_SETTINGS_DEFAULTS, ...data.ai };
        setGlobalSettings(prev => ({ ...prev, ...aiOnly }));
        localStorage.setItem('ofl_ai_global_settings', JSON.stringify(aiOnly));
        setBackendSynced(true);
      }
    });
  }, []);

  const refreshStatus = useCallback(() => {
    setStatusLoading(true);
    fetchProviderStatus()
      .then(s => setProviderStatus(s))
      .finally(() => setStatusLoading(false));
  }, []);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  const configuredCount = Object.values(providerStatus).filter(s => s?.configured).length;

  const handleSaveToken = () => {
    localStorage.setItem('ofl_admin_token', adminToken);
    setTokenSaved(true);
    setTimeout(() => setTokenSaved(false), 2000);
  };

  const handleSaveMcp = async () => {
    setMcpSaving(true);
    await new Promise(r => setTimeout(r, 400));
    localStorage.setItem('ofl_mcp_config', JSON.stringify(mcpConfig));
    setMcpSaved(true);
    setTimeout(() => setMcpSaved(false), 2500);
    setMcpSaving(false);
    showToast('MCP configuration saved locally.');
  };

  // Save AI-only settings — banner fields are intentionally excluded
  const handleSaveGlobal = async () => {
    setGlobalSaving(true);
    // Ensure no banner fields leak into the AI settings store
    const { bannerText, bannerColor, bannerTheme, ...aiOnly } = globalSettings;
    const result = await saveAiSettings(aiOnly);
    setGlobalSaving(false);
    setGlobalSaved(true);
    setTimeout(() => setGlobalSaved(false), 2500);

    if (result.warning) {
      showToast(result.warning, true);
    } else if (result.success) {
      setBackendSynced(true);
      showToast('AI settings saved and synced to backend.');
    } else {
      showToast(result.error || 'Settings saved locally only.', true);
    }
  };

  const TABS = [
    { id: 'generate',  label: 'Generate Content', icon: FiBookOpen },
    { id: 'keys',      label: 'API Keys',          icon: FiKey      },
    { id: 'mcp',       label: 'MCP Protocol',      icon: FiCode     },
    { id: 'settings',  label: 'Global Settings',   icon: FiSettings },
  ];

  return (
    <div className="space-y-8">
      {/* ── Header ── */}
      <div className="bg-gradient-to-r from-indigo-900 via-accent-navy to-purple-900 text-white rounded-[32px] p-8 relative overflow-hidden shadow-2xl">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 50%, #fff 1px, transparent 1px)',
          backgroundSize: '24px 24px',
        }} />
        <div className="relative z-10 flex items-start gap-6 flex-wrap">
          <div className="w-16 h-16 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0">
            <SafeIcon icon={FiBookOpen} className="w-8 h-8" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1 flex-wrap">
              <h2 className="font-serif text-2xl">AI Content Generation</h2>
              <span className="text-[9px] px-3 py-1 bg-accent-gold text-accent-navy rounded-full font-black uppercase tracking-widest">Super Admin</span>
              <span className="text-[9px] px-3 py-1 bg-purple-500/30 text-purple-200 rounded-full font-black uppercase tracking-widest">MCP Enabled</span>
              {backendSynced && (
                <span className="text-[9px] px-3 py-1 bg-green-500/30 text-green-200 rounded-full font-black uppercase tracking-widest flex items-center gap-1">
                  <SafeIcon icon={FiCloud} className="w-3 h-3" /> Synced
                </span>
              )}
            </div>
            <p className="text-white/60 text-sm font-light max-w-xl">
              Generate compassionate legacy articles, manage AI provider API keys, configure MCP tool-use, and tune global generation settings.
            </p>
          </div>
          <div className="flex gap-3 flex-shrink-0 flex-wrap">
            {[
              { label: 'Providers Ready', value: statusLoading ? '…' : configuredCount },
              { label: 'Total Providers', value: PROVIDERS.length },
              { label: 'MCP Tools',       value: '2' },
            ].map(stat => (
              <div key={stat.label} className="bg-white/10 border border-white/10 rounded-2xl p-4 text-center min-w-[90px]">
                <p className="text-2xl font-serif font-bold text-accent-gold">{stat.value}</p>
                <p className="text-[8px] font-black uppercase tracking-widest text-white/50 mt-0.5 leading-tight">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 mt-6 flex flex-wrap gap-2">
          {PROVIDERS.map(p => {
            const s = providerStatus[p.id];
            return (
              <div key={p.id} className="flex items-center gap-2 bg-white/10 border border-white/10 rounded-xl px-3 py-2">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s?.configured ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                <span className="text-[10px] text-white/80 font-medium">{p.label.split(' ')[0]}</span>
                <span className={`text-[8px] font-black uppercase ${s?.configured ? 'text-green-300' : 'text-red-300'}`}>
                  {statusLoading ? '…' : s?.configured ? s.source : 'not set'}
                </span>
              </div>
            );
          })}
          <button onClick={refreshStatus}
            className="flex items-center gap-1.5 bg-white/10 border border-white/10 rounded-xl px-3 py-2 text-[10px] text-white/70 hover:bg-white/20 transition-all font-black uppercase tracking-widest">
            <SafeIcon icon={FiRefreshCw} className={`w-3 h-3 ${statusLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Tab Navigation ── */}
      <div className="flex gap-1 bg-brand-light border border-brand-dark rounded-2xl p-1.5 overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all flex-1 justify-center ${
              activeTab === tab.id ? 'bg-accent-navy text-white shadow-lg' : 'text-text-muted hover:text-accent-navy'
            }`}>
            <SafeIcon icon={tab.icon} className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      <AnimatePresence mode="wait">
        <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>

          {activeTab === 'generate' && <AIArticleGenerator />}

          {activeTab === 'keys' && (
            <div className="space-y-5">
              {/* Admin token */}
              <div className="bg-white rounded-[24px] border border-brand-dark p-6 space-y-4">
                <div className="flex items-center gap-3 border-b border-brand-dark pb-4">
                  <div className="w-10 h-10 bg-accent-navy/5 text-accent-navy rounded-2xl flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiShield} className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-serif text-accent-navy">Admin Token</h4>
                    <p className="text-[10px] text-text-muted font-light">Required to set or clear runtime key overrides via the backend API</p>
                  </div>
                </div>
                <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl flex items-start gap-3">
                  <SafeIcon icon={FiInfo} className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700 font-light leading-relaxed">
                    Set <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono text-amber-800">AI_ADMIN_TOKEN</code> in your server <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono text-amber-800">.env</code> file.
                  </p>
                </div>
                <div className="flex gap-3">
                  <input type="password" value={adminToken} onChange={e => setAdminToken(e.target.value)}
                    placeholder="Enter AI_ADMIN_TOKEN value from server .env"
                    className="flex-1 px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm font-mono transition-colors" />
                  <button onClick={handleSaveToken}
                    className={`flex items-center gap-2 px-5 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      tokenSaved ? 'bg-green-500 text-white' : 'bg-accent-navy text-white hover:bg-accent-navy/90'
                    }`}>
                    {tokenSaved ? <SafeIcon icon={FiCheck} className="w-4 h-4" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
                    <span className="hidden sm:inline">{tokenSaved ? 'Saved' : 'Save'}</span>
                  </button>
                </div>
              </div>

              {/* .env reference */}
              <div className="bg-white rounded-[24px] border border-brand-dark p-6 space-y-3">
                <div className="flex items-center gap-3 mb-1">
                  <SafeIcon icon={FiServer} className="w-5 h-5 text-accent-navy" />
                  <h4 className="font-serif text-accent-navy">Environment Variables (Recommended)</h4>
                </div>
                <div className="bg-accent-navy rounded-2xl p-4 font-mono text-xs text-green-300 space-y-1 overflow-x-auto">
                  <p><span className="text-white/40"># server/.env</span></p>
                  {PROVIDERS.map(p => (
                    <p key={p.id}><span className="text-accent-gold">{p.envKey}</span>=<span className="text-green-400">your_{p.id}_key_here</span></p>
                  ))}
                  <p><span className="text-accent-gold">AI_ADMIN_TOKEN</span>=<span className="text-green-400">your_secure_admin_token</span></p>
                </div>
              </div>

              {/* Runtime key cards */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] font-black uppercase tracking-widest text-text-muted">Runtime Key Overrides</h4>
                  <button onClick={refreshStatus}
                    className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-accent-gold hover:underline">
                    <SafeIcon icon={FiRefreshCw} className={`w-3 h-3 ${statusLoading ? 'animate-spin' : ''}`} />
                    Refresh Status
                  </button>
                </div>
                {PROVIDERS.map(p => (
                  <ProviderKeyCard key={p.id} provider={p} status={providerStatus[p.id]}
                    adminToken={adminToken} onRefresh={refreshStatus} />
                ))}
              </div>

              <div className="p-4 bg-amber-50 border border-amber-100 rounded-2xl flex items-start gap-3">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700 font-light leading-relaxed">
                  <strong className="font-bold">Security note:</strong> Runtime key overrides are stored in server process memory only and cleared on restart.
                  API keys are <strong>never</strong> stored in the browser or transmitted beyond the server.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'mcp' && (
            <div className="space-y-5">
              <MCPConfigurationPanel config={mcpConfig} onChange={setMcpConfig}
                onSave={handleSaveMcp} saving={mcpSaving} saved={mcpSaved} />
              <div className="bg-white rounded-[24px] border border-brand-dark p-6 space-y-4">
                <h4 className="font-serif text-accent-navy border-b border-brand-dark pb-3">Built-in MCP Tools</h4>
                {[
                  {
                    name: 'legacy_context_lookup',
                    desc: 'Look up contextual information about a memorial or loved one from the One Final Legacy database.',
                    params: ['query: string', 'fields?: string[]'],
                    color: 'bg-orange-50 border-orange-100', badge: 'bg-orange-100 text-orange-700',
                  },
                  {
                    name: 'grief_resource_search',
                    desc: 'Search the grief and bereavement resource library for relevant articles and support information.',
                    params: ['topic: string', 'category?: support|planning|legal|financial|emotional|spiritual'],
                    color: 'bg-blue-50 border-blue-100', badge: 'bg-blue-100 text-blue-700',
                  },
                ].map(tool => (
                  <div key={tool.name} className={`p-4 border rounded-2xl ${tool.color}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <code className={`text-[10px] px-2.5 py-1 rounded-lg font-mono font-black ${tool.badge}`}>{tool.name}</code>
                    </div>
                    <p className="text-xs text-text-muted font-light mb-2">{tool.desc}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tool.params.map(p => (
                        <code key={p} className="text-[9px] px-2 py-0.5 bg-white rounded font-mono text-accent-navy border border-brand-dark">{p}</code>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-5">
              {/* AI-only global settings — no banner fields */}
              <GlobalSettingsPanel
                settings={globalSettings}
                onChange={setGlobalSettings}
                onSave={handleSaveGlobal}
                saving={globalSaving}
                saved={globalSaved}
                backendSynced={backendSynced}
              />

              {/* Server env reference */}
              <div className="bg-white rounded-[24px] border border-brand-dark p-6 space-y-3">
                <h4 className="font-serif text-accent-navy border-b border-brand-dark pb-3">Server Environment Reference</h4>
                <p className="text-xs text-text-muted font-light">
                  AI settings persist to <code className="bg-brand-light px-1.5 py-0.5 rounded font-mono">localStorage</code> and sync to the backend at{' '}
                  <code className="bg-brand-light px-1.5 py-0.5 rounded font-mono">PUT /api/settings/ai</code>.
                  Banner settings are managed via the <strong>Platform Maintenance Banner</strong> tab.
                </p>
                <div className="bg-accent-navy rounded-2xl p-4 font-mono text-xs space-y-1 overflow-x-auto">
                  {[
                    ['AI_MAX_TOKENS_LIMIT',   globalSettings.max_tokens],
                    ['AI_REQUEST_TIMEOUT_MS', (globalSettings.timeout_sec || 60) * 1000],
                  ].map(([k, v]) => (
                    <p key={k}><span className="text-accent-gold">{k}</span>=<span className="text-green-400">{v}</span></p>
                  ))}
                </div>
              </div>
            </div>
          )}

        </motion.div>
      </AnimatePresence>

      <SaveToast show={toast.show} message={toast.message} isWarning={toast.isWarning} />
    </div>
  );
};

export default AIContentGeneration;